import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box, Text, VStack, HStack, Button, Badge,
  Spinner, useColorModeValue, SimpleGrid,
  Table, Thead, Tbody, Tr, Th, Td,
  Icon, Flex, Progress, Divider, Collapse, Tooltip,
} from "@chakra-ui/react";
import {
  FaClipboardList, FaFileAlt, FaPlay, FaEye,
  FaChartBar, FaTrophy, FaCheckCircle, FaClock,
  FaChevronDown, FaChevronUp, FaLayerGroup,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useStudentContext } from "../B2CStudentContext";
import axios from "axios";
import { StudentAttemptSummary } from "../../../Platform/Common/types";
import { fmtLocal } from "../../../utils/dates";
import { openExamWindow } from "../../../utils/examWindow";
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from "recharts";

import {  createContext, useContext } from "react";

const AdminViewContext = createContext(false);

/* ========================================================= */
/* SHARED HELPERS -- module scope, pure, no component state  */
/* ========================================================= */

// Null/undefined = never attempted. 0 = a real attempt scored zero.
// These are NOT the same thing -- this was the source of the bug.
const hasAttempted = (qs: any) =>
  qs.last_score_percentage !== null && qs.last_score_percentage !== undefined;

const computeTrendData = (attempts: StudentAttemptSummary[]) =>
  attempts
    .filter(a =>
      a.status === "COMPLETED" &&
      a.auto_scoring_completed &&
      a.manual_scoring_completed &&
      a.percentage_score != null
    )
    .sort((a, b) => a.practice_attempt_id - b.practice_attempt_id)
    .map((a, idx) => ({
      attempt: idx + 1,
      score: parseFloat((a.percentage_score ?? 0).toFixed(1)),
    }));

const COLOR_SCHEMES = ["blue", "purple", "teal", "orange", "pink", "cyan"];
const getColor = (index: number) => COLOR_SCHEMES[index % COLOR_SCHEMES.length];


const statusColor = (s: string) =>
  s === "COMPLETED" ? "green" : s === "IN_PROGRESS" ? "purple" : "gray";

const AttemptsTable = ({
  attempts,
  onResumeAttempt,
  onViewAttempt,
  verboseIncomplete = false,
}: {
  attempts: StudentAttemptSummary[];
  onResumeAttempt: (id: number) => void;
  onViewAttempt: (id: number) => void;
  verboseIncomplete?: boolean; // exam list keeps "Left Incomplete – View" on desktop
}) => {
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const hideOnMobile = { base: "none", md: "table-cell" };
  const isAdminView = useContext(AdminViewContext);

  return (
    // Safety net: if a row is still too wide it scrolls instead of cutting off View
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th fontSize="2xs" px={{ base: 2, md: 4 }}>Started</Th>
            <Th fontSize="2xs" display={hideOnMobile}>Status</Th>
            <Th fontSize="2xs" display={hideOnMobile}>Mode</Th>
            <Th fontSize="2xs" px={{ base: 2, md: 4 }}>Score</Th>
            <Th fontSize="2xs" px={{ base: 2, md: 4 }}>Action</Th>
          </Tr>
        </Thead>
        <Tbody>
          {attempts.map((a) => {
            const scoringNeverFired = a.status === "COMPLETED" && !a.auto_scoring_completed;
            return (
              <Tr key={a.practice_attempt_id}>
                <Td fontSize="2xs" px={{ base: 2, md: 4 }}>
                  {/* Desktop: full date */}
                  <Box display={{ base: "none", md: "block" }}>
                    {fmtLocal(a.start_time, {
                      day: "2-digit", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit", hour12: true,
                    })}
                  </Box>
                  {/* Mobile: short date, with status + mode folded in underneath */}
                  <Box display={{ base: "block", md: "none" }}>
                    {fmtLocal(a.start_time, {
                      day: "2-digit", month: "short",
                      hour: "2-digit", minute: "2-digit", hour12: true,
                    })}
                    <HStack spacing={1} mt={1}>
                      <Badge colorScheme={statusColor(a.status)} fontSize="2xs">
                        {a.status.replace(/_/g, " ")}
                      </Badge>
                      <Text as="span" color={mutedText}>
                        {a.timed_flag ? "Timed" : "Untimed"}
                      </Text>
                    </HStack>
                  </Box>
                </Td>

                <Td display={hideOnMobile}>
                  <Badge colorScheme={statusColor(a.status)} fontSize="2xs">
                    {a.status.replace(/_/g, " ")}
                  </Badge>
                </Td>
                <Td fontSize="2xs" display={hideOnMobile}>
                  {a.timed_flag ? "Timed" : "Untimed"}
                </Td>

                <Td fontSize="2xs" px={{ base: 2, md: 4 }}>
                  {scoringNeverFired ? "—" :
                    a.manual_scoring_completed
                      ? `${a.percentage_score?.toFixed(1) ?? "—"}%`
                      : "Pending"}
                </Td>

                <Td px={{ base: 2, md: 4 }}>
                      {a.status === "IN_PROGRESS" ? (
                        isAdminView ? (
                          <Text fontSize="2xs" color={mutedText}>In progress</Text>
                        ) : (
                          <Button size="xs" leftIcon={<FaPlay />} colorScheme="orange"
                            onClick={() => onResumeAttempt(a.practice_attempt_id)}>
                            Resume
                          </Button>
                        )
                      ) : scoringNeverFired ? (
                    <Button size="xs" leftIcon={<FaEye />} colorScheme="orange" variant="outline"
                      onClick={() => onViewAttempt(a.practice_attempt_id)}>
                      {verboseIncomplete ? (
                        <>
                          <Box as="span" display={{ base: "none", md: "inline" }}>Left Incomplete – </Box>
                          View
                        </>
                      ) : "View"}
                    </Button>
                  ) : (
                    <Button size="xs" leftIcon={<FaEye />} colorScheme="blue" variant="outline"
                      isDisabled={!a.manual_scoring_completed}
                      onClick={() => onViewAttempt(a.practice_attempt_id)}>
                      View
                    </Button>
                  )}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

/* ========================================================= */
/* QUESTION SET CARD -- module scope                         */
/* ========================================================= */

const QuestionSetCard = ({
  qs,
  colorScheme,
  attempts,
  isLoadingAttempts,
  isTableOpen,
  onToggleAttempts,
  onResumeAttempt,
  onViewAttempt,
}: {
  qs: any;
  colorScheme: string;
  attempts: StudentAttemptSummary[] | undefined;
  isLoadingAttempts: boolean;
  isTableOpen: boolean;
  onToggleAttempts: () => void;
  onResumeAttempt: (id: number) => void;
  onViewAttempt: (id: number) => void;
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const inProgressBg = useColorModeValue("orange.50", "orange.900");
  const inProgressBorder = useColorModeValue("orange.200", "orange.700");
  const trendBg = useColorModeValue("gray.50", "gray.700");

  const attempted = hasAttempted(qs);
  const score = qs.last_score_percentage ?? 0;
  const inProgress = qs.is_attempt_in_progress ?? false;
  const scoreColor = score >= 80 ? "green" : score >= 50 ? "orange" : "red";
  const trendData = attempts ? computeTrendData(attempts) : [];

  return (
    <Box
     id={`qset-card-${qs.question_set_id}`}
     scrollMarginTop="80px"
      bg={inProgress ? inProgressBg : cardBg}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={inProgress ? inProgressBorder : borderColor}
      p={{ base: 3, md: 4 }}          // was p={4}
      position="relative"
    >
      {inProgress && (
        <Badge position="absolute" top={2} right={2}
          colorScheme="orange" fontSize="2xs" borderRadius="full" px={2}>
          In Progress
        </Badge>
      )}

      <VStack align="stretch" spacing={3}>
        <Text fontWeight="bold" fontSize="sm" pr={inProgress ? 16 : 0} noOfLines={2}>
          {qs.title}
        </Text>

        {attempted ? (
          <Box>
            <HStack justify="space-between" mb={1}>
              <Text fontSize="xs" color={mutedText}>Last Score</Text>
              <Text fontSize="xs" fontWeight="bold" color={`${scoreColor}.500`}>
                {score}%
              </Text>
            </HStack>
            <Progress value={score} size="sm" colorScheme={scoreColor} borderRadius="full" />
          </Box>
        ) : (
          <Box>
            <Text fontSize="xs" color={mutedText} mb={1}>Not attempted yet</Text>
            <Progress value={0} size="sm" colorScheme={colorScheme} borderRadius="full" />
          </Box>
        )}

        {/* Trend sparkline -- the analytics feel, right on the card */}
        {isLoadingAttempts && !attempts && (
          <HStack justify="center" py={2}><Spinner size="sm" /></HStack>
        )}
        {trendData.length >= 2 && (
          <Box bg={trendBg} borderRadius="md" p={2}>
            <ResponsiveContainer width="100%" height={60}>
              <LineChart data={trendData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <XAxis dataKey="attempt" tick={{ fontSize: 9 }} tickFormatter={(v) => `#${v}`} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} tickFormatter={(v) => `${v}%`} />
                <RechartsTooltip
                  formatter={(value) => [typeof value === "number" ? `${value.toFixed(1)}%` : "0%", "Score"]}
                  labelFormatter={(label) => `Attempt ${label}`}
                />
                <ReferenceLine y={50} stroke="gray" strokeDasharray="3 3" />
                <Line type="monotone" dataKey="score" stroke="#4299E1" strokeWidth={2}
                  dot={{ r: 2, fill: "#4299E1" }} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}

        <Button
          size="xs"
          variant="outline"
          colorScheme={colorScheme}
          leftIcon={<FaChartBar />}
          onClick={onToggleAttempts}
        >
          {isTableOpen ? "Hide Attempts" : "View All Attempts"}
        </Button>

        {/* Same table, same mechanism as before -- now lives inside the card */}
        <Collapse in={isTableOpen} animateOpacity>
          <Box pt={2}>
            {isLoadingAttempts ? (
              <HStack justify="center" py={4}><Spinner size="sm" /></HStack>
            ) : !attempts ? null : attempts.length === 0 ? (
              <Text color={mutedText} py={2} fontSize="xs">No attempts yet.</Text>
            ) : (
              <AttemptsTable
                attempts={attempts}
                onResumeAttempt={onResumeAttempt}
                onViewAttempt={onViewAttempt}
              />
            )}
          </Box>
        </Collapse>
      </VStack>
    </Box>
  );
};

/* ========================================================= */
/* GROUP SWIMLANE -- module scope, mirrors section-practice   */
/* ========================================================= */

const GroupSwimlane = ({
  group,
  index,
  isDefaultOpen,
  attemptsCache,
  loadingKeys,
  openGroups,
  toggleGroup,
  fetchAttempts,
  onResumeAttempt,
  onViewAttempt,
}: any) => {
  const [isOpen, setIsOpen] = useState(isDefaultOpen);
  const colorScheme = getColor(index);
  const headerBg = useColorModeValue(`${colorScheme}.100`, `${colorScheme}.900`);
  const headerHoverBg = useColorModeValue(`${colorScheme}.200`, `${colorScheme}.800`);
  const mutedText = useColorModeValue("gray.500", "gray.400");

  const sortedQsets = useMemo(
    () => [...group.question_sets].sort((a: any, b: any) => a.sequence_number - b.sequence_number),
    [group.question_sets]
  );

  const totalSets = sortedQsets.length;
  const attemptedQsets = sortedQsets.filter(hasAttempted);
  const attempted = attemptedQsets.length;
  const avgScore = attempted > 0
    ? Math.round(attemptedQsets.reduce((sum: number, qs: any) => sum + (qs.last_score_percentage ?? 0), 0) / attempted)
    : null;
  const inProgressCount = sortedQsets.filter((qs: any) => qs.is_attempt_in_progress).length;
  const groupProgress = totalSets > 0 ? Math.round((attempted / totalSets) * 100) : 0;

  // Eager-fetch trend data for every set in this group, but only once
  // it's actually open -- bounded to ~5 calls per group given your
  // 5-sets-per-group content design, not fired for collapsed groups.
  useEffect(() => {
    if (!isOpen) return;
    sortedQsets.forEach((qs: any) => {
      const key = `qset_${qs.question_set_id}`;
      fetchAttempts(key, { question_set_id: qs.question_set_id });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Box mb={4} borderRadius="xl" overflow="hidden" borderWidth="1px"
      borderColor={useColorModeValue(`${colorScheme}.100`, `${colorScheme}.800`)}>

      <Box
        bg={headerBg}
        px={4}
        py={3}
        borderLeft="5px solid"
        borderColor={`${colorScheme}.400`}
        cursor="pointer"
        _hover={{ bg: headerHoverBg }}
        onClick={() => setIsOpen(!isOpen)}
        transition="background 0.15s"
      >
        <Flex align="center">
          <HStack spacing={3} flex={1}>
            <Icon as={FaLayerGroup} color={`${colorScheme}.500`} />
            <Text fontWeight="bold" fontSize="md">{group.title}</Text>
            {inProgressCount > 0 && (
              <Badge colorScheme="orange" fontSize="2xs" borderRadius="full" px={2}>
                {inProgressCount} in progress
              </Badge>
            )}
          </HStack>

          <HStack spacing={4} mr={4} display={{ base: "none", md: "flex" }}>
            <HStack spacing={1}>
              <Icon as={FaCheckCircle} color="green.400" boxSize={3} />
              <Text fontSize="xs" color={mutedText}>{attempted}/{totalSets} done</Text>
            </HStack>
            {avgScore !== null && (
              <HStack spacing={1}>
                <Icon as={FaTrophy} color="yellow.500" boxSize={3} />
                <Text fontSize="xs" color={mutedText}>Avg {avgScore}%</Text>
              </HStack>
            )}
            <Box w="80px">
              <Progress value={groupProgress} size="xs" colorScheme={colorScheme} borderRadius="full" />
            </Box>
          </HStack>

          <Icon as={isOpen ? FaChevronUp : FaChevronDown} color={`${colorScheme}.500`} boxSize={4} />
        </Flex>
      </Box>

      <Collapse in={isOpen} animateOpacity>
        <Box px={{ base: 2, md: 4 }} py={4} bg={useColorModeValue("white", "gray.800")}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {sortedQsets.map((qs: any) => {
              const key = `qset_${qs.question_set_id}`;
              return (
                <QuestionSetCard
                  key={qs.question_set_id}
                  qs={qs}
                  colorScheme={colorScheme}
                  attempts={attemptsCache[key]}
                  isLoadingAttempts={!!loadingKeys[key]}
                  isTableOpen={!!openGroups[key]}
                  onToggleAttempts={() => {
                    toggleGroup(key);
                    if (!attemptsCache[key]) {
                      fetchAttempts(key, { question_set_id: qs.question_set_id });
                    }
                  }}
                  onResumeAttempt={onResumeAttempt}
                  onViewAttempt={onViewAttempt}
                />
              );
            })}
          </SimpleGrid>
        </Box>
      </Collapse>
    </Box>
  );
};

/* ========================================================= */
/* SECTION BLOCK -- outermost accordion, module scope         */
/* ========================================================= */

const SectionBlock = ({
  section,
  targetGroupId,
  suppressDefaultOpen,   // NEW
  attemptsCache,
  loadingKeys,
  openGroups,
  toggleGroup,
  fetchAttempts,
  onResumeAttempt,
  onViewAttempt,
}: any) => {
  const sortedGroups = useMemo(
    () => [...section.groups].sort((a: any, b: any) => a.sequence_number - b.sequence_number),
    [section.groups]
  );

  const totalSets = sortedGroups.reduce((acc: number, g: any) => acc + g.question_sets.length, 0);

  return (
    <Box mb={8}>
      {/* Plain heading, no card or accordion, so groups get the full width */}
      <HStack spacing={2} mb={3}>
        <Icon as={FaLayerGroup} color="blue.400" boxSize={4} />
        <Text fontWeight="bold" fontSize="lg">{section.title}</Text>
        <Badge colorScheme="blue" fontSize="2xs">{totalSets} sets</Badge>
      </HStack>

      {sortedGroups.map((group: any, idx: number) => (
        <GroupSwimlane
          key={group.practice_set_group_id}
          group={group}
          index={idx}
          isDefaultOpen={
            targetGroupId
              ? group.practice_set_group_id === targetGroupId
              : suppressDefaultOpen
                ? false
                : idx === 0
          }
          attemptsCache={attemptsCache}
          loadingKeys={loadingKeys}
          openGroups={openGroups}
          toggleGroup={toggleGroup}
          fetchAttempts={fetchAttempts}
          onResumeAttempt={onResumeAttempt}
          onViewAttempt={onViewAttempt}
        />
      ))}
    </Box>
  );
};

/* ========================================================= */
/* MAIN PAGE                                                  */
/* ========================================================= */

const B2CStudentAttemptListingsInner = ({ isAdminView = false }: { isAdminView?: boolean }) => {
  const navigate = useNavigate();
  const {
    entitlement_id,
    practice_set_id,
    question_set_id: targetQuestionSetId,
    practice_set_exam_id: targetExamId,
  } = useParams();

  const { studentContext } = useStudentContext();

  const cardBg = useColorModeValue("white", "gray.800");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headerBg = useColorModeValue("pink.50", "pink.900");
  const headerBorder = useColorModeValue("blue.100", "blue.800");
  const statBg = useColorModeValue("gray.50", "gray.750");
  const swimlaneHoverBg = useColorModeValue("gray.50", "gray.750");
  const swimlaneContentBg = useColorModeValue("white", "gray.800");

  const entitlement = useMemo(() => {
    return studentContext?.entitlements?.find(
      (e) => e.entitlement_id === Number(entitlement_id)
    );
  }, [studentContext, entitlement_id]);

  const studentPracticeSet = entitlement?.practice_set;

const [adminPracticeSet, setAdminPracticeSet] =
  useState<typeof studentPracticeSet | null>(null);
const [adminLoading, setAdminLoading] = useState(isAdminView);
const [adminError, setAdminError] = useState<string | null>(null);

const practiceSet = isAdminView ? adminPracticeSet : studentPracticeSet;

  const [attemptsCache, setAttemptsCache] = useState<Record<string, StudentAttemptSummary[]>>({});
  const [loadingKeys, setLoadingKeys] = useState<Record<string, boolean>>({});
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const apiUrl = process.env.REACT_APP_API_URL || "https://default-url.com";

  const fetchAttempts = useCallback(async (
    key: string,
    params: { question_set_id?: number; practice_set_exam_id?: number }
  ) => {
    setAttemptsCache(prevCache => {
      if (prevCache[key]) return prevCache; // already cached, no-op
      return prevCache;
    });
    // Re-check cache via functional state read is awkward for async guard,
    // so keep the original simple guard against the outer closure value:
    if (attemptsCache[key]) return;

    setLoadingKeys((prev) => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem("b2c_token");
      const response = await axios.get(`${apiUrl}/b2c/student/attempts`, {
        params: { ...params, entitlement_id: Number(entitlement_id) },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAttemptsCache((prev) => ({ ...prev, [key]: response.data.attempts }));
    } catch (error) {
      console.error("Failed loading attempts for", key, error);
    } finally {
      setLoadingKeys((prev) => ({ ...prev, [key]: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, entitlement_id, attemptsCache]);

  useEffect(() => {
    if (targetQuestionSetId) {
      const key = `qset_${targetQuestionSetId}`;
      fetchAttempts(key, { question_set_id: Number(targetQuestionSetId) });
      setOpenGroups((prev) => ({ ...prev, [key]: true }));
    }
    if (targetExamId) {
      const key = `exam_${targetExamId}`;
      fetchAttempts(key, { practice_set_exam_id: Number(targetExamId) });
      setOpenGroups((prev) => ({ ...prev, [key]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetQuestionSetId, targetExamId]);

  const toggleGroup = useCallback((key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleResumeAttempt = (practice_attempt_id: number) => {
    const url = `${window.location.origin}/mock-exam-window/${practice_attempt_id}`;
    openExamWindow(url);
  };

  const handleViewAttempt = (practice_attempt_id: number) => {
   // navigate(`/practice/app/practice-attempt/${practice_attempt_id}/view`);
    navigate(`/practice/app/practice-attempt/${practice_attempt_id}/view`, { state: { entitlement_id } });
  };

  // ── Page-level stats -- using the fixed hasAttempted check ──
  const allQsets = practiceSet?.sections.flatMap(s =>
    s.groups.flatMap(g => g.question_sets)
  ) ?? [];
  const totalSets = allQsets.length;
  const attemptedSets = allQsets.filter(hasAttempted);
  const completedCount = attemptedSets.length;
  const overallProgress = totalSets > 0 ? Math.round((completedCount / totalSets) * 100) : 0;
  const avgScore = completedCount > 0
    ? Math.round(attemptedSets.reduce((sum, qs) => sum + (qs.last_score_percentage ?? 0), 0) / completedCount)
    : null;
  const bestScore = completedCount > 0
    ? Math.max(...attemptedSets.map(qs => qs.last_score_percentage ?? 0))
    : null;


  const hasPracticeSet = !!practiceSet;
const scrollDone = useRef(false);

// Which element and which attempts-cache key this deep link points at
const deepLink = useMemo(() => {
  if (targetQuestionSetId) {
    return { elementId: `qset-card-${targetQuestionSetId}`, key: `qset_${targetQuestionSetId}` };
  }
  if (targetExamId) {
    return { elementId: `exam-row-${targetExamId}`, key: `exam_${targetExamId}` };
  }
  return null;
}, [targetQuestionSetId, targetExamId]);

const scrollToTarget = useCallback(() => {
  if (!deepLink || scrollDone.current) return;
  const el = document.getElementById(deepLink.elementId);
  if (!el) return;
  scrollDone.current = true;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}, [deepLink]);

// Main path: wait until the target's attempts have loaded and nothing is
// still fetching (sparklines change card heights), then let the Collapse
// animation settle before scrolling. One-shot per mount.
useEffect(() => {
  if (!deepLink || !hasPracticeSet || scrollDone.current) return;
  const anyLoading = Object.values(loadingKeys).some(Boolean);
  if (anyLoading || !attemptsCache[deepLink.key]) return;
  const t = setTimeout(scrollToTarget, 350);
  return () => clearTimeout(t);
}, [deepLink, hasPracticeSet, attemptsCache, loadingKeys, scrollToTarget]);

// Fallback: if the attempts call fails or is very slow, still bring the target into view
useEffect(() => {
  if (!deepLink || !hasPracticeSet) return;
  const t = setTimeout(scrollToTarget, 2000);
  return () => clearTimeout(t);
}, [deepLink, hasPracticeSet, scrollToTarget]);

useEffect(() => {
  if (!isAdminView) return;
  let cancelled = false;
  (async () => {
    try {
      const token = localStorage.getItem("b2c_token");
      const response = await axios.get(
        `${apiUrl}/b2c/admin/entitlements/${Number(entitlement_id)}/progress`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!cancelled) setAdminPracticeSet(response.data.practice_set);
    } catch (error) {
      console.error("Failed loading entitlement progress", error);
      if (!cancelled) setAdminError("Unable to load progress for this entitlement.");
    } finally {
      if (!cancelled) setAdminLoading(false);
    }
  })();
  return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isAdminView, entitlement_id, apiUrl]);

if (isAdminView && adminLoading) {
  return <Box p={10} textAlign="center"><Spinner size="lg" /></Box>;
}

if (!practiceSet) {
  return <Box p={10}><Text>{adminError ?? "Practice Set not found."}</Text></Box>;
}

  if (!practiceSet) {
    return <Box p={10}><Text>Practice Set not found.</Text></Box>;
  }

// Deep-link (question set): find the group containing it.
// Safe because a question_set_id appears exactly once across the whole
// practice set -- no ambiguity.
let targetGroupId: number | undefined;

if (targetQuestionSetId) {
  outer: for (const section of practiceSet.sections) {
    for (const group of section.groups) {
      if (group.question_sets.some((qs) => qs.question_set_id === Number(targetQuestionSetId))) {
        targetGroupId = group.practice_set_group_id;
        break outer;
      }
    }
  }
}

// Deep-link (exam): exams sit directly on the practice set, no
// section/group lookup needed -- the exam accordion key is already
// derived straight from practice_set_exam_id further down.
const targetExamKey = targetExamId ? `exam_${targetExamId}` : undefined;

  const sortedSections = [...practiceSet.sections].sort(
    (a, b) => a.sequence_number - b.sequence_number
  );

  return (
      <AdminViewContext.Provider value={isAdminView}>
    <Box maxW="1400px" mx="auto" p={{ base: 3, md: 6 }}>

      {/* ── Enriched page header ── */}
      <Box bg={headerBg} border="1px solid" borderColor={headerBorder} borderRadius="2xl" p={6} mb={8}>
        <Flex align="start" gap={6} direction={{ base: "column", md: "row" }}>
          <VStack align="start" spacing={3} flex={1}>
            <Badge colorScheme="blue" px={3} py={1} borderRadius="full">{isAdminView ? "Student Progress (Admin)" : "My Progress"}</Badge>
            <Text fontSize="2xl" fontWeight="bold">{practiceSet.title}</Text>
            <Text fontSize="sm" color={mutedText}>
              Track your attempts and score trends across all question sets and mock exams.
            </Text>
            <Box w="100%">
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs" color={mutedText}>Overall Completion</Text>
                <Text fontSize="xs" fontWeight="semibold"
                  color={overallProgress === 100 ? "green.500" : overallProgress > 50 ? "blue.500" : "orange.500"}>
                  {completedCount}/{totalSets} sets attempted
                </Text>
              </Flex>
              <Progress value={overallProgress} size="sm"
                colorScheme={overallProgress === 100 ? "green" : overallProgress > 50 ? "blue" : "orange"}
                borderRadius="full" />
            </Box>
          </VStack>

          <HStack spacing={3} flexShrink={0} align="stretch">
            {[
              { label: "Attempted", value: `${completedCount}/${totalSets}`, icon: FaCheckCircle, color: "green" },
              { label: "Avg Score", value: avgScore !== null ? `${avgScore}%` : "—", icon: FaChartBar, color: "blue" },
              { label: "Best Score", value: bestScore !== null ? `${bestScore}%` : "—", icon: FaTrophy, color: "yellow" },
            ].map((stat) => (
              <Box key={stat.label} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}
                p={4} textAlign="center" minW="90px">
                <Icon as={stat.icon} color={`${stat.color}.500`} boxSize={4} mb={1} />
                <Text fontSize="xl" fontWeight="bold">{stat.value}</Text>
                <Text fontSize="xs" color={mutedText}>{stat.label}</Text>
              </Box>
            ))}
          </HStack>
        </Flex>
      </Box>

      {/* ── Question Set Attempts: Section > Group swimlane > Cards ── */}
      <VStack align="stretch" spacing={0} mb={10}>
        <Text fontSize="xl" fontWeight="bold" mb={4}>Question Set Attempts</Text>

        {sortedSections.map((section) => (
          <SectionBlock
            key={section.practice_set_section_id}
            section={section}
            targetGroupId={targetGroupId}
            suppressDefaultOpen={!!targetExamId}   // NEW
            attemptsCache={attemptsCache}
            loadingKeys={loadingKeys}
            openGroups={openGroups}
            toggleGroup={toggleGroup}
            fetchAttempts={fetchAttempts}
            onResumeAttempt={handleResumeAttempt}
            onViewAttempt={handleViewAttempt}
          />
        ))}
      </VStack>

      {/* ── Mock Exam Attempts -- unchanged ── */}
      <VStack align="stretch" spacing={5}>
        <Text fontSize="xl" fontWeight="bold">Mock Exam Attempts</Text>

        <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}>
          {practiceSet.exams.length === 0 ? (
            <Box px={5} py={8} textAlign="center" color={mutedText}>
              <Text fontSize="sm">No mock exams available.</Text>
            </Box>
          ) : (
            practiceSet.exams
              .sort((a, b) => a.sequence_number - b.sequence_number)
              .map((exam) => {
                const key = `exam_${exam.practice_set_exam_id}`;
                const isOpen = openGroups[key];
                const isUnlimited = exam.max_attempts == null;
                const exhausted = !isUnlimited && (exam.remaining_attempts ?? 0) <= 0;
                const attempts = attemptsCache[key];
                const isLoading = loadingKeys[key];

                return (
                  <Box key={exam.practice_set_exam_id} 
                    id={`exam-row-${exam.practice_set_exam_id}`}
                    scrollMarginTop="80px"
                  borderBottom="1px solid" borderColor={borderColor}
                    _last={{ borderBottom: "none" }}>
                    <Box
                      px={4} py={3} cursor="pointer"
                      _hover={{ bg: swimlaneHoverBg }}
                      onClick={() => {
                        toggleGroup(key);
                        if (!attemptsCache[key]) {
                          fetchAttempts(key, { practice_set_exam_id: exam.practice_set_exam_id });
                        }
                      }}
                      transition="background 0.15s"
                    >
                      <Flex align="center">
                        <HStack spacing={3} flex={1}>
                          <Icon as={FaFileAlt} color="purple.500" boxSize={4} />
                          <Text fontWeight="semibold" fontSize="sm">{exam.title}</Text>
                          {exhausted && (
                            <Badge colorScheme="red" fontSize="2xs" borderRadius="full" px={2}>Exhausted</Badge>
                          )}
                        </HStack>
                        <HStack spacing={4} mr={4} display={{ base: "none", md: "flex" }}>
                          <HStack spacing={1}>
                            <Icon as={FaClock} color={mutedText} boxSize={3} />
                            <Text fontSize="xs" color={mutedText}>{exam.total_duration_minutes} mins</Text>
                          </HStack>
                          <Badge colorScheme={exhausted ? "red" : "purple"} fontSize="2xs">
                            {isUnlimited ? "Unlimited" : `${exam.remaining_attempts ?? 0} of ${exam.max_attempts} left`}
                          </Badge>
                        </HStack>
                        <Icon as={isOpen ? FaChevronUp : FaChevronDown} color="gray.400" boxSize={3} />
                      </Flex>
                    </Box>

                    <Collapse in={isOpen} animateOpacity>
                      <Box px={{ base: 2, md: 5 }} py={4} bg={swimlaneContentBg}>
                        {isLoading ? (
                          <HStack justify="center" py={6}><Spinner size="md" /></HStack>
                        ) : !attempts ? null : attempts.length === 0 ? (
                          <Text color={mutedText} py={4} fontSize="sm">No attempts yet.</Text>
                        ) : (
                          <AttemptsTable
                            attempts={attempts}
                            onResumeAttempt={handleResumeAttempt}
                            onViewAttempt={handleViewAttempt}
                            verboseIncomplete
                          />
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })
          )}
        </Box>
      </VStack>
    </Box>
    </AdminViewContext.Provider>
  );
};

// ===== [MY PROGRESS HYDRATION] START =====
// Same route serves every entitlement, so React reuses the instance and the
// attempts cache (keyed by question set only) leaks between entitlements.
// Keying on entitlement_id forces a clean remount on entitlement change.
const B2CStudentAttemptListings = ({ isAdminView = false }: { isAdminView?: boolean }) => {
  const { entitlement_id } = useParams();
  return <B2CStudentAttemptListingsInner key={entitlement_id} isAdminView={isAdminView} />;
};
// ===== [MY PROGRESS HYDRATION] END =====

export default B2CStudentAttemptListings;